import { useTranslation } from 'react-i18next';
import { InstagramFeedPost } from '@anytag/graphql';
import { THEME } from '@anytag/shared-constants';
import { MediaPreviewWithMetrics } from '@anytag/shared-atoms';
import { bigIntFormatter } from '@anytag/shared-utils';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { ImageSlider, InfluencerInstagramAnalyticsPostDetailsModal } from '@anytag/shared-molecules';
import { PostDetailsType } from '@anytag/shared-types';
import { AnalyticsSelectedAccountInfo } from '../../../types';
import { AnalyticsFeedPostsWrapper, AnalyticsFeedPostsWrapperProps } from '../../../shared';
import { useAnalyticsProfileSearch } from '../../../hooks';

export interface AnalyticsInstagramDashboardFeedPostsTemplateProps
  extends Pick<AnalyticsFeedPostsWrapperProps, 'title' | 'help' | 'moreDetailProps'> {
  data?: readonly InstagramFeedPost[];
}

export const AnalyticsInstagramDashboardFeedPostsTemplate = ({
  data = [],
  ...restProps
}: AnalyticsInstagramDashboardFeedPostsTemplateProps) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  return (
    <AnalyticsFeedPostsWrapper
      dataNotAvailable={!data.length}
      title={t('HeaderColumn.Feed Posts')}
      help={t('Tooltip.dashboardFeedPosts')}
      {...restProps}
    >
      <ImageSlider total={data.length} css={{ paddingLeft: '16px' }}>
        {data.map(({ id, thumbnail, likes, comments }) => (
          <MediaPreviewWithMetrics
            key={id}
            src={thumbnail}
            css={THEME.mediaPreview.default}
            onClick={() => setPostDetailsParam(id)}
            statistics={[
              { label: t('Like'), value: bigIntFormatter(likes, 0, { k: true, m: true }) },
              { label: t('General.Comments'), value: bigIntFormatter(comments, 0, { k: true, m: true }) }
            ]}
          />
        ))}
      </ImageSlider>
    </AnalyticsFeedPostsWrapper>
  );
};

export interface AnalyticsInstagramDashboardFeedPostsProps {
  brandAccountName?: string;
  data?: readonly InstagramFeedPost[];
  accountId: AnalyticsSelectedAccountInfo['id'];
}

export const AnalyticsInstagramDashboardFeedPosts = ({
  data = [],
  accountId,
  brandAccountName
}: AnalyticsInstagramDashboardFeedPostsProps) => {
  const { filter, params } = useAnalyticsProfileSearch();

  return (
    <>
      <AnalyticsInstagramDashboardFeedPostsTemplate
        data={data}
        moreDetailProps={{
          to: '/analytics/$brandAccountId/$socialMedia/$socialMediaAccountId/dashboard/instagram_feed_post',
          params,
          search: {
            filter: {
              brandAccountName,
              endDate: filter.endDate,
              startDate: filter.startDate
            }
          }
        }}
      />

      <InfluencerInstagramAnalyticsPostDetailsModal
        isAnalytics
        posts={data.map(({ id }): PostDetailsType => ({ id, mainSocialAccountId: accountId }))}
      />
    </>
  );
};
